import type { RequiredKeys } from '@/helpers/types';
import AcordoExternoDto from '../dto/AcordoExternoDto';
import { StatusProposta } from '../enums';
import MeioPagamento from '../enums/MeioPagamento';
import { IPromessa } from '../IPromessa';
import BoletoResource from './BoletoResource';
import DetalhesPropostaResource from './DetalhesPropostaResource';
import DividaAcordoExternoResource from './DividaAcordoExternoResource';
import OfertaResource from './OfertaResource';
import ParcelasGerenciadasResource from './ParcelasGerenciadasResource';

export default class AcordoExternoResource implements IPromessa {
  id: number;
  divida: DividaAcordoExternoResource;
  assessoria?: string;
  quantidadeParcelas: number;
  quantidadeParcelasMaisEntrada: number;
  valorParcela: number;
  valorEntrada: number;
  dataPrimeiroBoleto: Date;
  parcelas: BoletoResource[];
  dataProposta: Date;
  detalhe?: DetalhesPropostaResource;
  detalheDescricao?: string;
  isAcordoExterno: boolean;
  meioPagamentoEscolhido: MeioPagamento;
  meiosPagamentoDemaisParcelas: MeioPagamento[];
  meiosPagamentoPrimeiraParcela: MeioPagamento[];
  negociacaoParcial?: boolean;
  oferta?: OfertaResource;
  parcelasGerenciadas: ParcelasGerenciadasResource;
  percentualDesconto: number;
  possuiEntrada: boolean;
  possuiTermoAcordo?: boolean;
  status: number;
  urlTermoAcordo?: string;
  valorDesconto: number;
  valorTotal: number;
  valorTotalCalculado: number;
  dataQuitacao?: Date;
  permiteCancelamento: boolean;

  constructor(api:string, dto: AcordoExternoDto) {
    this.isAcordoExterno = true;
    this.id = dto.id;
    this.divida = new DividaAcordoExternoResource(api, dto);
    this.assessoria = dto.assessoria ?? undefined;
    this.quantidadeParcelas = dto.quantidadeParcelas;
    this.valorParcela = dto.valorParcela;
    this.valorEntrada = dto.valorEntrada;
    this.dataPrimeiroBoleto = new Date(dto.dataPrimeiraParcela);
    this.parcelas = dto.parcelas?.map((e) => new BoletoResource(e)) || [];

    this.dataProposta = new Date(dto.dataPrimeiraParcela);
    this.status = dto.parcelas.every((p) => p.quitada)
      ? StatusProposta.Quitada
      : StatusProposta.Aceita;
    this.valorTotal = dto.valorEntrada + dto.valorParcela * dto.quantidadeParcelas;
    this.valorDesconto = 0;
    this.valorTotalCalculado = this.valorTotal;
    this.possuiEntrada = dto.valorEntrada > 0;
    this.possuiTermoAcordo = false;
    this.quantidadeParcelasMaisEntrada = dto.quantidadeParcelas + (dto.valorEntrada > 0 ? 1 : 0);
    this.permiteCancelamento = false;
    this.meioPagamentoEscolhido = MeioPagamento.NaoIdentificado;
    this.meiosPagamentoPrimeiraParcela = [MeioPagamento.NaoIdentificado];
    this.meiosPagamentoDemaisParcelas = [MeioPagamento.NaoIdentificado];
    this.parcelasGerenciadas = new ParcelasGerenciadasResource({
      primeiraParcela: true,
      demaisParcelas: true,
    });
    this.percentualDesconto = 0;
  }

  get proximaParcelaPendente(): BoletoResource | undefined {
    const parcelasPendentesComData = this.parcelas
      ?.filter((p): p is RequiredKeys<BoletoResource, 'dataVencimento'> => (
        !!p.dataVencimento && !p.pago
      ));

    if (!parcelasPendentesComData?.length) return undefined;

    return parcelasPendentesComData
      .reduce(
        (prv, cur) => (prv.dataVencimento > cur.dataVencimento ? cur : prv),
        parcelasPendentesComData[0],
      );
  }

  get acordoQuitado(): boolean {
    return this.parcelas.length > 0 && this.parcelas.every((parcela) => parcela.quitada);
  }

  get possuiParcela(): boolean {
    return !!this.parcelas
      ?.some((parcela) => (
        parcela.boletoEmitido
        || parcela.pixEmitido
        || parcela.pago
        || parcela.quitada
      ));
  }

  get reconhecePagamentoImediato() {
    if (this.meioPagamentoEscolhido === MeioPagamento.Pix) {
      return this.divida.parametros?.pixReconhecimentoImediato === true;
    }

    return false;
  }
}
